import {
  createI18n,
  I18n,
  Composer,
  Locale,
  NamedValue,
  NumberOptions,
} from 'vue-i18n'

import { CURRENCY_FALLBACK } from './currencies'
import { FALLBACK_LOCALE as fallbackLocale } from '@/shared/config'
import options from '@/shared/i18n/options'
import { usePreferencesStore } from '@/popup/store/PreferencesStore'
import {
  numberFormats,
  Formatters,
  generateNumberFormat,
} from '@/shared/i18n/formats/number'
import { assertNotUndefined } from '@/shared/validators'
import { datetimeFormats } from '@/shared/i18n/formats/datetime'

export let i18n: I18n
export let rtf: Intl.RelativeTimeFormat

const units: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000_000],
  ['month', 2_628_000_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
  ['second', 1000],
]

export async function setupI18n(): Promise<I18n> {
  const preferencesStore = usePreferencesStore()
  const locale = preferencesStore.locale
  const numberFormats_ = numberFormats(locale, preferencesStore.currentCurrency)

  i18n = createI18n({ ...options, locale, numberFormats: numberFormats_ })

  if (locale !== fallbackLocale) {
    await loadLocale(locale)
  }

  setLocale(locale)

  return i18n
}

export function setLocale(locale: Locale): void {
  ;(i18n.global as unknown as Composer).locale.value = locale
  document.querySelector('html')?.setAttribute('lang', locale)
  setRelativeTimeFormat(locale)
}

export function setRelativeTimeFormat(locale: Locale): void {
  if (Intl && Intl.RelativeTimeFormat) {
    rtf = new Intl.RelativeTimeFormat(locale, { style: 'narrow' })
  }
}

export async function loadLocale(locale: Locale): Promise<void> {
  const messages = await import(
    `@/translations/${locale.replace('-', '_')}.json`
  )

  // update messages for new locale
  ;(i18n.global as unknown as Composer).setLocaleMessage(
    locale,
    messages.default
  )
}

export function t(key: string, interpolations?: NamedValue): string {
  if (!i18n) {
    return key
  }

  return interpolations
    ? (i18n.global as unknown as Composer).t(key, interpolations)
    : (i18n.global as unknown as Composer).t(key)
}

export function n(
  value: number,
  key: Formatters = 'decimal',
  options?: NumberOptions
): string {
  if (!i18n) {
    return value.toString()
  }

  if (options) {
    options.key = key
    return (i18n.global as unknown as Composer).n(value, options)
  } else {
    return (i18n.global as unknown as Composer).n(value, key)
  }
}

export function d(
  value?: string | number | Date,
  format?: 'narrow' | 'short' | 'long' | 'longDate' | 'time'
): string | undefined {
  if (!value) {
    return
  }
  assertNotUndefined(value, 'Value is undefined while translating')

  const formats = datetimeFormats()
  const formatOptions = formats?.[format || 'short']

  return new Date(value).toLocaleDateString(undefined, formatOptions)
}

export const setNumberFormats = (
  locale: Locale,
  currencySymbol = CURRENCY_FALLBACK
): void => {
  const formats = generateNumberFormat(currencySymbol)
  ;(i18n?.global as unknown as Composer)?.setNumberFormat(locale, formats)
}

/**
 * Show time in relative format (ie. "1 day ago"). Works for both future and past dates.
 *
 * @param date - date in milliseconds
 * @returns - string
 */
export const relativeTime = (date: number) => {
  if (!rtf) {
    return ''
  }

  const elapsed = date - Date.now()

  for (const [unit, amount] of units) {
    if (Math.abs(elapsed) > amount || unit === 'second') {
      return rtf.format(Math.round(elapsed / amount), unit)
    }
  }
}
